import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import { AxiosError } from 'axios';
import { ChangeEventHandler, FormEvent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { FormField, RuleType } from '../FormField/FormField';
import InputField from '../Skapa/InputField/InputField';
import { useTranslation } from 'react-i18next';
import { isAxiosProblemDetailsError } from './types';
import { getBaseUrl, pathJoin } from '../../utils/url';
import { StatusCode } from '@mvecom/common/enums/StatusCode';

import styles from './LoginPage.module.scss';
interface ILoginPageContentRight {
  closeMenu: () => void;
  skapaPrefix: string;
  login: (email: string, password: string) => void;
  loginLoading: boolean;
  loginError: AxiosError | undefined;
}

function profilePagesUrl(subpath: string, addOpenedByParam: boolean) {
  const href = window.location.href;
  const path = pathJoin(getBaseUrl(href), 'profile', subpath);
  const url = new URL(path);

  if (addOpenedByParam) {
    url.searchParams.set('openedBy', window.location.href);
  }

  return url.toString();
}

const Urls = {
  migrate: () => profilePagesUrl('migrate-password', false),
  resetPassword: () => profilePagesUrl('reset-password', false),
  signUp: () => profilePagesUrl('sign-up', true),
};

const EMAIL_VALIDATION = {
  maxLength: 80,
  regex: /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
};

export const Login = ({
  closeMenu,
  skapaPrefix,
  login,
  loginLoading,
  loginError,
}: ILoginPageContentRight): ReactElement => {
  const { t } = useTranslation('profile');

  const [email, setEmail] = useState('');
  const onEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => setEmail(e.target.value), []);
  const [password, setPassword] = useState('');
  const onPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => setPassword(e.target.value),
    [setPassword],
  );

  const problemDetails = useMemo(
    () => (isAxiosProblemDetailsError(loginError) ? loginError.response?.data : undefined),
    [loginError],
  );

  useEffect(() => {
    if (problemDetails?.type === 'accountExpired' || problemDetails?.type === 'accountMigrated') {
      window.location.href = Urls.migrate();
    }
  }, [closeMenu, email, password, problemDetails?.type]);

  const handleLogin = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      login(email, password);
    },
    [email, login, password],
  );

  const goToSignUpPage = useCallback(() => {
    window.location.href = Urls.signUp();
  }, []);

  return (
    <div className={styles.container}>
      <h2>{t('loginPage.loginPageHeader')}</h2>
      <p>{t('loginPage.loginPageHeaderDescription')}</p>

      {loginError && problemDetails?.status !== StatusCode.UNAUTHORIZED && (
        <InlineMessage
          prefix={skapaPrefix}
          body={t('loginPage.errorMessage')}
          data-testid="error-message"
          title={t('loginPage.errorMessageTitle')}
          variant="negative"
          className={styles.inlineMessage}
        />
      )}

      {loginError && problemDetails?.status === StatusCode.UNAUTHORIZED && (
        <InlineMessage
          prefix={skapaPrefix}
          body={t('loginPage.errorIncorrectInfoMessage')}
          title={t('loginPage.errorIncorrectInfoMessage')}
          data-testid="error-message"
          variant="negative"
          className={styles.inlineMessage}
        />
      )}

      <form onSubmit={handleLogin}>
        <fieldset>
          <FormField
            validationRules={[
              {
                type: RuleType.MAX_LENGTH,
                value: EMAIL_VALIDATION.maxLength,
                message: t('formInputField.invalidEmail'),
              },
              {
                type: RuleType.REGEX,
                value: EMAIL_VALIDATION.regex,
                message: t('formInputField.invalidEmail'),
              },
            ]}
            hideCharacterLimit
          >
            <InputField
              disabled={loginLoading}
              id="login-email"
              autoComplete="email"
              type="text"
              name="email"
              value={email}
              label={t('formInputField.credentialEmail')}
              onChange={onEmailChange}
            />
          </FormField>
          <FormField>
            <InputField
              disabled={loginLoading}
              id="login-password"
              autoComplete="current-password"
              type="password"
              name="password"
              label={t('formInputField.credentialPassword')}
              value={password}
              onChange={onPasswordChange}
            />
          </FormField>
        </fieldset>

        <a data-testid="reset-password" href={Urls.resetPassword()}>
          {t('loginPage.forgotPassword')}
        </a>

        <Button
          className={styles.loginButton}
          prefix={skapaPrefix}
          fluid
          htmlType="submit"
          loading={loginLoading}
          data-testid="login"
          text={t('loginPage.loginButton')}
          type="primary"
        />
        <Button
          prefix={skapaPrefix}
          disabled={loginLoading}
          fluid
          onClick={goToSignUpPage}
          data-testid="create-account"
          text={t('loginPage.createAccountButton')}
          type="secondary"
        />
      </form>
    </div>
  );
};
